import {
  Box,
  Button,
  Card,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Log, getPendingBankAccount, getPendingBankAccountDetails, BankAccountApprover } from "../../services/services";
import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { StyledTableRow } from "../../components/TableRow";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import { CustomTableCell } from "../../components/TableCell";
import CommonDialog from "../../components/CustomDialog";
import CircularLoader from "../../components/circularLoader";
import { styles } from "../../utils/styles";
import RejectBankAccountPopup from "./Dialogs/RejectBankAccountPopup";
import BankAccountDetails from "./Dialogs/BankAccountDetail";
import { pageNumber, rowsPerPage } from "../../utils/constants";
import Pagination from "../../components/Pagination";

const TABLE_HEAD = [
  { id: "account_number", label: "Account Number" },
  { id: "account_name", label: "Account Name" },
  { id: "bank", label: "Bank Name" },
  { id: "action", label: "Action" },
  { id: "details" },
];

const PendingBankAccount = ({ userRole }) => {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [bankAccountList, setBankAccountList] = useState({ results: [], count: 0 });
  const [loaded, setLoaded] = useState(false);
  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });

  // dialogs
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [pendingDetails, setPendingDetails] = useState(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredList = bankAccountList.results || []

  const isItemNotFound = filteredList.length === 0;

  const queryPage = (queryParams) => {
    setLoaded(false);
    getPendingBankAccount(
      (res) => {
        setBankAccountList(res);
      },
      setLoaded,
      queryParams
    );
    setPagination(queryParams);
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: event.target.value,
    });
  };

  useEffect(() => {
    queryPage(pagination);
  }, []);

  const handleCloseDialog = (shouldResetDetails) => {
    setOpenRejectDialog(false);
    if (shouldResetDetails === true) setPendingDetails(null);
  };

  return (
    <Box>
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />

                <TableBody>
                  {filteredList && filteredList.length > 0 && filteredList.map((row) => {
                    const { id, account_number, account_name, bank, action } = row;

                    return (
                      <StyledTableRow
                        hover
                        key={id}
                      >
                        <CustomTableCell field={account_number} />
                        <CustomTableCell field={account_name} />
                        <CustomTableCell field={bank} />
                        <CustomTableCell field={action} />
                        <TableCell>
                          <Button
                            variant="text"
                            color="secondary"
                            onClick={() => {
                              setGlobalLoading(true);
                              getPendingBankAccountDetails(id, setPendingDetails, () => setGlobalLoading(false)
                              );
                            }}
                          >
                            View Detail
                          </Button>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper>
                          <Typography
                            gutterBottom
                            align="center"
                            variant="subtitle1"
                          >
                            There are no pending bank accounts at the moment.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={bankAccountList.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <RejectBankAccountPopup
          open={openRejectDialog}
          onClose={handleCloseDialog}
          id={selectedId}
          setBankAccountList={(res) => setBankAccountList(res)}
          setLoaded={setLoaded}
        />
      )}

      {pendingDetails && (
        <CommonDialog
          title="Bank Account Details"
          onClose={() => setPendingDetails(null)}
          renderActions={() =>
            userRole.includes("Approve/Reject") && (
              <DialogActions>
                <Button
                  variant="outlined"
                  // color="inherit"
                  onClick={() => {
                    setOpenRejectDialog(true);
                    setSelectedId(pendingDetails.id);
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setGlobalLoading(true);
                    Log.info(
                      `Click button [Approve] - Approve Business Entity by Id: ${pendingDetails.id}`
                    );
                    await BankAccountApprover(
                      pendingDetails.id,
                      "approve",
                      (res) => setBankAccountList(res),
                      setLoaded
                    );
                    setPendingDetails(null);
                    setGlobalLoading(false);
                  }}
                  sx={{ ...styles.customButton }}
                >
                  Approve
                </Button>
              </DialogActions>
            )
          }
        >
          <BankAccountDetails detailsData={pendingDetails} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default PendingBankAccount;
